import { useEffect, useMemo, useState } from 'react';
import { useWithdrawalTargetRisk } from '../features/withdrawal-target-risk';
import type { WithdrawalTargetRiskUserDTO } from '../features/withdrawal-target-risk';
import { fenToYuan } from '../lib/format';

const TARGET_TYPE_LABELS: Record<string, string> = {
  alipay: '支付宝',
  bank_card: '银行卡',
  red_packet_openid: '红包 openid',
};

const USER_STATUS_LABELS: Record<string, string> = {
  active: '正常',
  disabled: '已停用',
};

const WITHDRAWAL_STATUS_LABELS: Record<string, string> = {
  pending: '待审核',
  approved: '已通过',
  paying: '打款中',
  manual_processing: '人工处理',
  payout_failed: '打款失败',
  rejected: '已拒绝',
  settled: '已结算',
};

const PAGE_SIZE_OPTIONS = [20, 50, 100];

function formatTargetType(value: string): string {
  return TARGET_TYPE_LABELS[value] ?? value;
}

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function userLabel(item: WithdrawalTargetRiskUserDTO): string {
  return item.email || item.nickname || `用户 ${item.user_id}`;
}

export function WithdrawalTargetRiskPage() {
  const {
    items,
    total,
    loading,
    error,
    targetType,
    keyword,
    userId,
    minDistinctUsers,
    page,
    pageSize,
    selected,
    setSelected,
    users,
    usersTotal,
    usersLoading,
    submitting,
    setFilter,
    setPage,
    setPageSize,
    resetFilters,
    reload,
    bulkDisable,
  } = useWithdrawalTargetRisk();
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [disableReason, setDisableReason] = useState('');

  useEffect(() => {
    setSelectedUserIds([]);
  }, [selected?.target_hash]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const selectableUserIds = useMemo(
    () => users.filter((item) => item.status !== 'disabled').map((item) => item.user_id),
    [users],
  );
  const allSelected = selectableUserIds.length > 0
    && selectableUserIds.every((id) => selectedUserIds.includes(id));

  const toggleUser = (userID: number, checked: boolean) => {
    setSelectedUserIds((current) => {
      if (checked) return Array.from(new Set([...current, userID]));
      return current.filter((id) => id !== userID);
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedUserIds(checked ? selectableUserIds : []);
  };

  const handleBulkDisable = async () => {
    const ok = await bulkDisable(selectedUserIds, disableReason);
    if (ok) {
      setSelectedUserIds([]);
      setDisableReason('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-red-500 mb-3">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-gray-700">
          类型
          <select
            value={targetType}
            onChange={(e) => setFilter('targetType', e.target.value)}
            className="ml-2 h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            <option value="">全部</option>
            <option value="alipay">支付宝</option>
            <option value="bank_card">银行卡</option>
            <option value="red_packet_openid">红包 openid</option>
          </select>
        </label>
        <label className="text-sm text-gray-700">
          账号
          <input
            value={keyword}
            onChange={(e) => setFilter('keyword', e.target.value)}
            className="ml-2 h-9 w-44 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </label>
        <label className="text-sm text-gray-700">
          用户 ID
          <input
            value={userId}
            onChange={(e) => setFilter('userId', e.target.value)}
            className="ml-2 h-9 w-32 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </label>
        <label className="text-sm text-gray-700">
          最少用户数
          <input
            value={minDistinctUsers}
            onChange={(e) => setFilter('minDistinctUsers', e.target.value)}
            className="ml-2 h-9 w-28 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </label>
        <button
          onClick={resetFilters}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
        >
          重置
        </button>
      </div>

      <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left font-medium">提现账号</th>
              <th className="px-4 py-3 text-left font-medium">类型</th>
              <th className="px-4 py-3 text-right font-medium">关联用户</th>
              <th className="px-4 py-3 text-right font-medium">提现次数</th>
              <th className="px-4 py-3 text-right font-medium">金额</th>
              <th className="px-4 py-3 text-left font-medium">最近提现</th>
              <th className="px-4 py-3 text-left font-medium">最近告警</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item) => (
              <tr
                key={item.target_hash}
                onClick={() => setSelected(item)}
                className={`cursor-pointer ${selected?.target_hash === item.target_hash ? 'bg-primary/5' : 'hover:bg-gray-50'}`}
              >
                <td className="px-4 py-3 font-medium text-gray-900">{item.target_masked || '-'}</td>
                <td className="px-4 py-3 text-gray-600">{formatTargetType(item.target_type)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-900">{item.distinct_user_count}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600">{item.withdrawal_count}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600">¥{fenToYuan(item.total_amount_fen)}</td>
                <td className="px-4 py-3 text-gray-600">{formatDateTime(item.latest_withdrawal_at)}</td>
                <td className="px-4 py-3 text-gray-600">{formatDateTime(item.last_alerted_at)}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                  暂无提现账号风控数据
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
        <span>共 {total} 条</span>
        <div className="flex items-center gap-2">
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="h-8 rounded-md border border-gray-300 px-2 text-sm"
          >
            {PAGE_SIZE_OPTIONS.map((value) => (
              <option key={value} value={value}>{value} / 页</option>
            ))}
          </select>
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="h-8 rounded-md border border-gray-300 px-3 disabled:opacity-50"
          >
            上一页
          </button>
          <span className="tabular-nums">{page} / {totalPages}</span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="h-8 rounded-md border border-gray-300 px-3 disabled:opacity-50"
          >
            下一页
          </button>
        </div>
      </div>

      {selected && (
        <div className="space-y-4 border-t border-gray-200 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {selected.target_masked || '-'} 关联账号
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {formatTargetType(selected.target_type)}，共 {usersTotal} 个用户。
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={disableReason}
                onChange={(e) => setDisableReason(e.target.value)}
                placeholder="停用原因"
                className="h-9 w-72 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
              <button
                onClick={handleBulkDisable}
                disabled={submitting || selectedUserIds.length === 0 || disableReason.trim() === ''}
                className="h-9 rounded-md bg-red-600 px-4 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                批量停用
              </button>
            </div>
          </div>

          <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => toggleAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-medium">用户</th>
                  <th className="px-4 py-3 text-left font-medium">状态</th>
                  <th className="px-4 py-3 text-right font-medium">提现次数</th>
                  <th className="px-4 py-3 text-right font-medium">金额</th>
                  <th className="px-4 py-3 text-left font-medium">最近状态</th>
                  <th className="px-4 py-3 text-left font-medium">最近提现</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((item) => (
                  <tr key={item.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(item.user_id)}
                        disabled={item.status === 'disabled'}
                        onChange={(e) => toggleUser(item.user_id, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50 disabled:opacity-40"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{userLabel(item)}</div>
                      <div className="text-xs text-gray-400">ID {item.user_id}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{USER_STATUS_LABELS[item.status] ?? item.status}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-600">{item.withdrawal_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-600">¥{fenToYuan(item.total_amount_fen)}</td>
                    <td className="px-4 py-3 text-gray-600">{WITHDRAWAL_STATUS_LABELS[item.latest_status] ?? item.latest_status}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDateTime(item.latest_withdrawal_at)}</td>
                  </tr>
                ))}
                {usersLoading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">加载中...</td>
                  </tr>
                )}
                {!usersLoading && users.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">暂无关联用户</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
